import React, { useState, useEffect } from 'react';
import { buttonStyles } from '@/utils/synczstyle';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'syncz_cookie_consent';
let inMemoryConsent = false;

const hasConsent = (): boolean => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            if (window.localStorage.getItem(COOKIE_CONSENT_KEY) === 'accepted') {
                return true;
            }
        }
        if (typeof document !== 'undefined' && document.cookie) {
            if (document.cookie.includes(`${COOKIE_CONSENT_KEY}=accepted`)) {
                return true;
            }
        }
    } catch {}
    return inMemoryConsent;
};

export const CookieConsent: React.FC = () => {
    const [visible, setVisible] = useState(() => !hasConsent());

    useEffect(() => {
        if (!hasConsent()) {
            const timer = setTimeout(() => {
                if (!hasConsent()) {
                    setVisible(true);
                }
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            setVisible(false);
        }
    }, []);

    const handleAccept = () => {
        inMemoryConsent = true;
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
            }
            if (typeof document !== 'undefined') {
                document.cookie = `${COOKIE_CONSENT_KEY}=accepted; path=/; max-age=31536000; SameSite=Lax`;
            }
        } catch {}
        setVisible(false);
    };

    if (!visible || hasConsent()) return null;

    return (
        <aside aria-label="Cookie notice" className="fixed bottom-4 right-4 z-[90] max-w-sm sm:max-w-md w-[calc(100%-2rem)] animate-in slide-in-from-bottom-3 duration-500">
            <div className="flex items-center justify-between gap-3 p-3 sm:py-2.5 sm:px-3.5 rounded-2xl border border-white/10 bg-neutral-900/95 backdrop-blur-xl shadow-2xl text-xs text-neutral-300">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-1.5 bg-white/5 border border-white/10 rounded-lg text-neutral-300 shrink-0">
                        <Cookie size={15} />
                    </div>
                    <p className="text-[11px] sm:text-xs text-neutral-300 line-clamp-2 leading-tight">
                        We use essential cookies for authentication & site performance. See{' '}
                        <Link to="/privacy" className="text-white underline hover:text-neutral-300">Privacy</Link>.
                    </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    <button 
                        onClick={handleAccept}
                        className={`${buttonStyles.primary} !px-3 !py-1.5 !text-xs !rounded-lg whitespace-nowrap`}
                    >
                        Accept
                    </button>
                    <button
                        onClick={handleAccept}
                        aria-label="Dismiss cookie notice"
                        className="p-1 text-neutral-500 hover:text-white rounded-lg transition-colors"
                        title="Dismiss"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>
        </aside>
    );
};
